import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useDropzone } from 'react-dropzone';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';

const API = process.env.REACT_APP_API_URL || '';

const QUALITY = [
  {
    key: 'low',
    label:  { en: 'Low Quality',    ar: 'جودة منخفضة',   fa: 'کیفیت پایین' },
    desc:   { en: 'Smallest file',  ar: 'أصغر حجم',      fa: 'کوچکترین فایل' },
  },
  {
    key: 'medium',
    label:  { en: 'Balanced',       ar: 'متوازن',         fa: 'متعادل' },
    desc:   { en: 'Good size/quality tradeoff', ar: 'توازن الحجم والجودة', fa: 'تعادل حجم و کیفیت' },
  },
  {
    key: 'high',
    label:  { en: 'High Quality',   ar: 'جودة عالية',    fa: 'کیفیت بالا' },
    desc:   { en: 'Best quality, larger file', ar: 'أفضل جودة، حجم أكبر', fa: 'بهترین کیفیت، فایل بزرگتر' },
  },
];

const T = {
  drop:       { en: 'Drag any file here or click to select', ar: 'اسحب أي ملف هنا أو انقر للاختيار',  fa: 'هر فایلی را اینجا بکشید یا کلیک کنید' },
  supported:  { en: 'PDF · Images · DOCX · PPTX · Any file', ar: 'PDF · صور · DOCX · PPTX · أي ملف', fa: 'PDF · تصویر · DOCX · PPTX · هر فایل' },
  compress:   { en: '⚡ Compress Now', ar: '⚡ ضغط الآن',   fa: '⚡ فشرده‌سازی' },
  compressing:{ en: 'Compressing…',   ar: 'جاري الضغط…',   fa: 'در حال فشرده‌سازی...' },
  patience:   { en: 'Large files can take several minutes — keep this tab open', ar: 'الملفات الكبيرة قد تستغرق دقائق — لا تغلق هذا التبويب', fa: 'فایل‌های بزرگ ممکن است چند دقیقه طول بکشند — این تب را باز نگه دارید' },
  success:    { en: 'Compressed successfully!', ar: 'تم الضغط بنجاح!', fa: 'فشرده‌سازی موفق!' },
  failed:     { en: 'Compression failed',       ar: 'فشل الضغط',       fa: 'فشرده‌سازی ناموفق' },
  remove:     { en: 'Remove file', ar: 'إزالة الملف', fa: 'حذف فایل' },
  qualityLbl: { en: 'Quality Level', ar: 'مستوى الجودة', fa: 'سطح کیفیت' },
  original:   { en: 'Original',    ar: 'الأصل',     fa: 'اصلی' },
  compressed: { en: 'Compressed',  ar: 'المضغوط',   fa: 'فشرده‌شده' },
  saved:      { en: 'saved',       ar: 'توفير',     fa: 'صرفه‌جویی' },
  noSavings:  { en: 'File already optimised — returned as-is', ar: 'الملف محسّن بالفعل', fa: 'فایل از قبل بهینه است' },
};

const t = (key, lang) => T[key]?.[lang] || T[key]?.en || key;

function fmtSize(bytes) {
  if (bytes >= 1024 * 1024) return (bytes / 1024 / 1024).toFixed(1) + ' MB';
  return (bytes / 1024).toFixed(0) + ' KB';
}

export default function CompressCard({ lang }) {
  const { token } = useAuth();
  const [file, setFile]       = useState(null);
  const [quality, setQuality] = useState('medium');
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  const [result, setResult]   = useState(null); // { original, compressed }
  const timerRef = useRef(null);

  const rtl = lang === 'ar' || lang === 'fa';

  const onDrop = useCallback((accepted) => {
    if (accepted.length > 0) { setFile(accepted[0]); setResult(null); }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop, multiple: false });

  useEffect(() => {
    if (loading) {
      setElapsed(0);
      timerRef.current = setInterval(() => setElapsed(s => s + 1), 1000);
    } else {
      clearInterval(timerRef.current);
    }
    return () => clearInterval(timerRef.current);
  }, [loading]);

  const fmtElapsed = (s) => s >= 60 ? `${Math.floor(s/60)}m ${s%60}s` : `${s}s`;

  const handleCompress = async () => {
    if (!file) return;
    setLoading(true);
    setProgress(0);
    setResult(null);

    const safeExt = file.name.split('.').pop().toLowerCase();
    const safeName = `file.${safeExt}`;
    const fd = new FormData();
    fd.append('file', file, safeName);
    fd.append('quality', quality);

    try {
      const headers = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const res = await axios.post(`${API}/api/convert/compress`, fd, {
        responseType: 'blob',
        headers,
        timeout: 600000,
        onUploadProgress: (e) => {
          if (e.total) setProgress(Math.round((e.loaded / e.total) * 50));
        },
        onDownloadProgress: (e) => {
          if (e.total) setProgress(50 + Math.round((e.loaded / e.total) * 48));
          else setProgress(prev => prev < 98 ? prev + 1 : 98);
        },
      });

      setProgress(99);

      const compressedSize = res.data.size;
      const disposition = res.headers['content-disposition'] || '';
      const match = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      const filename = match ? match[1].replace(/['"]/g, '') : `compressed_${file.name}`;

      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      window.URL.revokeObjectURL(url);

      setResult({ original: file.size, compressed: compressedSize });
      toast.success(t('success', lang), {
        style: { background: '#13131f', color: '#00d4aa', border: '1px solid rgba(0,212,170,0.3)' },
      });
    } catch (err) {
      let msg = t('failed', lang);
      if (err.response?.data instanceof Blob) {
        try { const txt = await err.response.data.text(); msg = JSON.parse(txt).detail || msg; } catch {}
      } else if (err.response?.data?.detail) {
        msg = err.response.data.detail;
      }
      toast.error(msg);
    } finally {
      setLoading(false);
      setProgress(0);
    }
  };

  const savings = result && result.original > 0
    ? Math.round((1 - result.compressed / result.original) * 100)
    : null;

  const ACCENT = '#10b981';

  return (
    <div>
      {/* Quality picker */}
      <div style={{ marginBottom: '16px' }}>
        <div style={{
          fontSize: '12px', fontWeight: 700, color: 'rgba(255,255,255,0.4)',
          textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px',
          textAlign: rtl ? 'right' : 'left',
        }}>
          {t('qualityLbl', lang)}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          {QUALITY.map(q => (
            <button
              key={q.key}
              onClick={() => setQuality(q.key)}
              style={{
                background: quality === q.key ? `${ACCENT}22` : 'rgba(255,255,255,0.03)',
                border: `1px solid ${quality === q.key ? ACCENT : 'rgba(255,255,255,0.1)'}`,
                borderRadius: '8px', padding: '10px 14px',
                textAlign: rtl ? 'right' : 'left',
                cursor: 'pointer', transition: 'all 0.15s',
              }}
            >
              <div style={{ fontSize: '13px', fontWeight: 600, color: quality === q.key ? '#fff' : 'rgba(255,255,255,0.75)' }}>
                {q.label[lang] || q.label.en}
              </div>
              <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.35)', marginTop: '2px' }}>
                {q.desc[lang] || q.desc.en}
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Drop zone */}
      <div
        {...getRootProps()}
        style={{
          border: `2px dashed ${isDragActive ? ACCENT : 'rgba(255,255,255,0.15)'}`,
          borderRadius: '12px', padding: '32px 20px', textAlign: 'center',
          cursor: 'pointer',
          background: isDragActive ? `${ACCENT}10` : 'rgba(255,255,255,0.02)',
          transition: 'all 0.2s', marginBottom: '16px',
        }}
      >
        <input {...getInputProps()} />
        <div style={{ fontSize: '32px', marginBottom: '8px' }}>🗜️</div>
        {file ? (
          <div style={{ color: 'rgba(255,255,255,0.85)', fontSize: '14px', fontWeight: 600 }}>
            {file.name}
            <div style={{ color: 'rgba(255,255,255,0.35)', fontSize: '12px', marginTop: '4px', fontWeight: 400 }}>
              {fmtSize(file.size)}
            </div>
          </div>
        ) : (
          <>
            <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: '14px' }}>{t('drop', lang)}</div>
            <div style={{ color: 'rgba(255,255,255,0.3)', fontSize: '12px', marginTop: '6px' }}>
              {t('supported', lang)}
            </div>
          </>
        )}
      </div>

      {file && (
        <button
          onClick={() => { setFile(null); setResult(null); }}
          style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.3)', fontSize: '12px', cursor: 'pointer', padding: '0 0 12px 0', display: 'block' }}
        >
          {t('remove', lang)}
        </button>
      )}

      {/* Result stats */}
      {result && (
        <div style={{
          marginBottom: '12px', padding: '12px',
          background: 'rgba(16,185,129,0.08)',
          border: '1px solid rgba(16,185,129,0.25)',
          borderRadius: '8px',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: 'rgba(255,255,255,0.7)' }}>
            <span>{t('original', lang)}: {fmtSize(result.original)}</span>
            <span>{t('compressed', lang)}: {fmtSize(result.compressed)}</span>
          </div>
          <div style={{ textAlign: 'center', marginTop: '8px', fontWeight: 700, fontSize: '15px',
            color: savings > 0 ? ACCENT : 'rgba(255,255,255,0.4)' }}>
            {savings > 0
              ? `↓ ${savings}% ${t('saved', lang)}`
              : t('noSavings', lang)}
          </div>
        </div>
      )}

      <button
        className="btn-primary"
        onClick={handleCompress}
        disabled={!file || loading}
        style={{
          opacity: !file ? 0.5 : 1,
          background: (!file || loading) ? undefined : `linear-gradient(135deg, ${ACCENT}, #059669)`,
        }}
      >
        {loading
          ? <><span className="spinner" />{t('compressing', lang)} {progress > 0 ? `${progress}%` : ''} {elapsed > 0 ? `(${fmtElapsed(elapsed)})` : ''}</>
          : t('compress', lang)
        }
      </button>

      {loading && progress > 0 && (
        <div style={{ marginTop: '10px', background: 'rgba(255,255,255,0.06)', borderRadius: '6px', overflow: 'hidden', height: '4px' }}>
          <div style={{ height: '100%', borderRadius: '6px', background: ACCENT, width: `${progress}%`, transition: 'width 0.3s ease' }} />
        </div>
      )}
      {loading && elapsed >= 15 && (
        <div style={{ marginTop: '8px', fontSize: '11px', color: 'rgba(255,255,255,0.4)', textAlign: 'center' }}>
          {t('patience', lang)}
        </div>
      )}
    </div>
  );
}
